<template>
    <div class="search-bar">
        <div class="search-bar-wrap">
            <v-form class="search-bar-form" @submit="handleSubmit($event)">
                <div class="search-field">
                    <v-text-field
                        v-model:model-value="searchString$"
                        name="searchString"
                        :label="t('form_label_search_bar')"
                        class="search-bar-form-field"
                        :placeholder="t('form_label_search_bar')"
                        :persistent-clear="!!selectSearchString"
                        variant="outlined"
                        prepend-inner-icon="search"
                        density="comfortable"
                        :clearable="!!selectSearchString"
                        @click:clear="handleClear"
                        @update:model-value="handleSearch"
                        @update:focused="handleOnFocus"
                    >
                    </v-text-field>
                </div>
                <div class="search-type-wrap">
                    <v-switch
                        v-model:model-value="searchType$"
                        class="search-type"
                        color="#FFF"
                        false-value="machines"
                        true-value="implements"
                        :ripple="false"
                        density="comfortable"
                        @update:model-value="handleChangeSearchType"
                    >
                        <template #prepend>
                            <v-icon
                                :color="selectSearchType === 'machines' ? '#FFF' : '#fff6f65e'"
                                style="opacity: 1"
                                icon="agriculture"
                            ></v-icon>
                        </template>
                        <template #label
                        ><v-icon
                            :color="selectSearchType === 'implements' ? '#FFF' : '#fff6f65e'"
                            icon="build_circle"
                        ></v-icon
                        ></template>
                    </v-switch>
                </div>
            </v-form>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import useUIStore from '../store/ui.store';
import { storeToRefs } from 'pinia';
import { includes, toString, trim, delay, debounce } from 'lodash';
import { useRouter, type RouteLocationNormalizedLoadedGeneric } from 'vue-router';

import { computed, watch } from 'vue';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import useMachinesStore from '../../machines/store/machine.store';
import useImplementStore from '../../implements/store/implement.store';
import { MachineService } from '../../machines/services/machine.service';
import { ImplementService } from '../../implements/services/implements.service';
import type { SubmitEventPromise } from 'vuetify';

const { t } = useI18n();
const uiStore = useUIStore();
const machineStore = useMachinesStore();
const implementStore = useImplementStore();
const router = useRouter();

const machineServices = new MachineService();
const implementServices = new ImplementService();
const localStorage = new LocalStorageService();

const { selectSearchString, selectSearchType } = storeToRefs(uiStore);

const searchType$ = computed({
    get() {
        if (localStorage.getItem('searchType') === 'implements') {
            uiStore.setSearchType('implements');
        }
        if (localStorage.getItem('searchType') === 'machines') {
            uiStore.setSearchType('machines');
        }
        return selectSearchType.value;
    },
    set(value) {
        localStorage.setItem('searchType', value);
        uiStore.setSearchType(value);
    },
});

const searchString$ = computed({
    get() {
        return selectSearchString.value;
    },
    set(value) {
        machineStore.setMachineFiltersValues({
            name: value,
            model: ['All'],
            tractor_loader_only: null,
            min_base_weight: null,
        });
        implementStore.setImplementFiltersValues({
            name: value,
            model: ['All'],
            category: ['All'],
            min_base_weight: null,
            tractor_loader_only: null,
        });
        uiStore.setSearchString(value);
    },
});

watch(
    router.currentRoute,
    (currentRoute: RouteLocationNormalizedLoadedGeneric) => {
        if (includes(currentRoute.path, 'implements')) {
            uiStore.setSearchString('');
            searchType$.value = 'implements';
        }
        if (includes(toString(currentRoute.path), 'machines')) {
            uiStore.setSearchString('');
            searchType$.value = 'machines';
        }
    },
    { deep: true }
);

const handleClear = (): void => {
    router.replace({ query: {} });
    machineStore.resetMachineFiltersValues();
    implementStore.resetImplementFiltersValues();
    performSearch('', true);
    uiStore.setSearchOnFocus(false);
};

const handleChangeSearchType = (): void => {
    // router.replace({ query: { search: '' } })
    machineStore.resetMachineFiltersValues();
    implementStore.resetImplementFiltersValues();
    performSearch('', true);
};

const performSearch = async (value: string, reset = false): Promise<void> => {
    const searchValue = trim(value ?? '');

    if (reset || value === null) {
        if (selectSearchType.value === 'machines') {
            await machineServices.loadMachineGridData({
                page_num: 1,
                page_size: 10,
                exact_match: 'false',
            });
        }

        if (selectSearchType.value === 'implements') {
            await implementServices.loadImplementGridData({
                page_num: 1,
                page_size: 10,
                exact_match: 'false',
            });
        }

        uiStore.setSearchOnFocus(false);
        return;
    }

    // Only search when user types at least 2 characters
    if (searchValue.length < 2) {
        uiStore.setSearchOnFocus(false);
        return;
    }

    if (selectSearchType.value === 'machines') {
        await machineServices.loadMachineGridData({
            page_num: 1,
            page_size: 10,
            name: searchValue,
            exact_match: 'false',
        });
    }

    if (selectSearchType.value === 'implements') {
        await implementServices.loadImplementGridData({
            page_num: 1,
            page_size: 10,
            name: searchValue,
            exact_match: 'false',
        });
    }

    uiStore.setSearchOnFocus(true);
};

const handleSearch = debounce((value: string) => {
    performSearch(value);
}, 500);

const handleOnFocus = (onFocus: boolean): void => {
    if (onFocus === false) {
        delay(() => uiStore.setSearchOnFocus(onFocus), 1000);
    }
};

const handleSubmit = async (event: SubmitEventPromise): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    router.push({
        name: selectSearchType.value === 'machines' ? 'MachinesHome' : 'ImplementsHome',
        query: { search: selectSearchString.value },
    });
    uiStore.setSearchOnFocus(false);
};
</script>
<style lang="scss">
.show-all {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 24px 16px 8px;
}
.search-bar {
    min-width: 380px;
    &-form {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        .search-type-wrap {
            border: 1px solid white;
            height: 48px;
            border-left: 0;
            border-radius: 0 4px 4px 0;
            position: relative;
            left: -3px;
            padding: 0 8px;
            min-width: 120px;
            .v-switch.v-input {
                height: 48px;
                position: relative;
                top: -1px;
            }
        }
        .search-type {
            .v-input__prepend {
                margin-inline-end: 8px;
            }
            .v-input__details {
                display: none;
            }
        }
        .search-field {
            border-color: #fff;
            min-width: 263px;
            .v-input__details {
                display: none;
            }
            .v-input {
                .v-input__control {
                    .v-field {
                        color: #fff !important;
                        .v-field__outline {
                            border-radius: 4px 0 0 4px;
                            --v-field-border-width: 1px !important;
                            --v-field-border-opacity: 1;
                        }
                        .v-field__outline__end {
                            border-inline-end-width: 0 !important;
                        }
                    }
                }
            }
        }
    }
}
</style>
